import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { useSearchParams } from "react-router";
import { getAntiforgeryToken, portalFetch } from "../../api/http";
import { missingText } from "../../i18n/texts";
import { useFocusHeading } from "./focusHeading";

/** Claves de texto del selector (las que lee Portal/Organizaciones.razor). */
export const organizationsTextKeys = [
  "ORG_SWITCH_TITLE",
  "ORG_SWITCH_LABEL",
  "ORG_SWITCH_GO",
  "TEAM_ERR_FORBIDDEN",
  "CP_SUPPORT_SCOPE_FIXED",
];

type Choice = { support: boolean; options: { id: number; name: string }[]; selected: number | null };

// Código de la carga de la página: el servidor responde 403 con la misma página cuando rechaza el cambio de empresa
// (sin pertenencia, empresa inactiva o vista de soporte), como el Forbid() del portal actual.
const loaded = { path: location.pathname.toLowerCase(), status: navigationStatus() };

function navigationStatus(): number {
  const entry = performance.getEntriesByType?.("navigation")[0] as
    (PerformanceNavigationTiming & { responseStatus?: number }) | undefined;
  return entry?.responseStatus ?? 200;
}

/**
 * Selector de empresa (/portal/organizaciones): lista las empresas vigentes sin cambiar nada al abrir. Cambiar de
 * empresa es un POST clásico de formulario con recarga (reescribe la cookie), el mismo del menú de la cabecera; el
 * servidor valida la pertenencia y lleva al destino. En la vista de soporte el ámbito está fijo.
 */
export function OrganizationsPage({ locale }: { locale: string }) {
  const [params] = useSearchParams();
  const texts = useQuery({
    queryKey: ["texts", locale, "organizations"],
    queryFn: () =>
      portalFetch<Record<string, string>>(
        `/api/portal/texts/${locale}?prefixes=${encodeURIComponent(organizationsTextKeys.join(","))}`,
      ),
  });
  const choice = useQuery({
    queryKey: ["session-organizations"],
    queryFn: () => portalFetch<Choice>("/api/portal/session/organizations"),
    staleTime: 0,
  });
  const L = (key: string) => texts.data?.[key] ?? missingText(key);
  const title = texts.data ? L("ORG_SWITCH_TITLE") : "";
  useEffect(() => {
    if (title) document.title = title;
  }, [title]);
  useFocusHeading(!!texts.data && !!choice.data);
  if (!texts.data || !choice.data) return null;

  const rejected = loaded.path === "/portal/organizaciones" && loaded.status === 403;
  const forbidden = choice.data.support || rejected;
  // La recuperación de Contactos o Etiquetas conserva su destino a través del selector (solo para la misma empresa).
  const recoveryReturn = params.get("ReturnUrl");
  const recoveryOrganization = params.get("returnOrg");
  return (
    <div className="wrap narrow">
      <h1>{title}</h1>
      {forbidden ? (
        <div className="alert alert-error" role="alert">
          {L(choice.data.support ? "CP_SUPPORT_SCOPE_FIXED" : "TEAM_ERR_FORBIDDEN")}
        </div>
      ) : (
        <form method="post" action={location.pathname + location.search}>
          <input type="hidden" name="_handler" value="organizaciones" />
          <input type="hidden" name="__RequestVerificationToken" value={getAntiforgeryToken() ?? ""} />
          {recoveryReturn !== null ? (
            <>
              <input type="hidden" name="form.ReturnUrl" value={recoveryReturn} />
              <input type="hidden" name="form.ReturnOrganizationId" value={recoveryOrganization ?? ""} />
            </>
          ) : null}
          <div className="field">
            <label htmlFor="org">{L("ORG_SWITCH_LABEL")}</label>
            <select id="org" name="form.OrganizationId" defaultValue={choice.data.selected ?? undefined}>
              {choice.data.options.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.name}
                </option>
              ))}
            </select>
          </div>
          <button type="submit" className="button">
            {L("ORG_SWITCH_GO")}
          </button>
        </form>
      )}
    </div>
  );
}
